import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BiArrowBack } from "react-icons/bi";
import { BsCheckCircle, BsLock, BsTruck, BsCreditCard, BsCalendarEvent, BsPerson, BsEnvelope, BsTelephone } from "react-icons/bs";
import { FiEdit } from "react-icons/fi";
import Container from "../components/Container";
import Meta from "../components/Meta";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { base_url, config } from "../utils/axiosConfig";
import {
  createAnOrder,
  deleteUserCart,
  getUserCart,
  resetState,
  getUserAddresses,
  saveUserAddress
} from "../features/user/userSlice";
import { getAProduct } from "../features/products/productSlilce";
import { authService } from "../features/user/userService";
import { trackInitiateCheckout, trackAddPaymentInfo, trackPurchase } from "../utils/metaPixel";
import { toast } from 'react-toastify';

// Schema for guest user checkout
let guestCheckoutSchema = yup.object({
  firstname: yup.string().required("First Name is Required"),
  lastname: yup.string().required("Last Name is Required"),
  email: yup.string().email("Invalid email format").required("Email is Required"),
  mobile: yup.string().required("Mobile is Required"),
  address: yup.string().required("Address Details are Required"),
  pincode: yup.number("Pincode No is Required").required().positive().integer(),
});

// Schema for normal user checkout
let shippingSchema = yup.object({
  firstname: yup.string().required("First Name is Required"),
  lastname: yup.string().required("Last Name is Required"),
  address: yup.string().required("Address Details are Required"),
  pincode: yup.number("Pincode No is Required").required().positive().integer(),
});

const CheckoutInstant = () => {
  const { productId } = useParams(); // Get product ID from URL params
  const dispatch = useDispatch();
  const authCartState = useSelector((state) => state?.auth?.cartProducts);
  const guestCartState = useSelector((state) => state?.auth?.guestCart);
  const authState = useSelector((state) => state?.auth);
  const userState = useSelector((state) => state.auth.user);
  const userAddresses = useSelector((state) => state?.auth?.userAddresses);
  const productState = useSelector((state) => state?.product?.singleproduct); // For single product checkout
  const [totalAmount, setTotalAmount] = useState(null);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState({
    razorpayPaymentId: "",
    razorpayOrderId: "",
  });
  const [hasPreorderItems, setHasPreorderItems] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false); // New state for login prompt
  const [guestEmail, setGuestEmail] = useState(""); // New state for guest email
  const [orderId, setOrderId] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [saveAddressForFuture, setSaveAddressForFuture] = useState(false);
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const [disableNormalForm, setDisableNormalForm] = useState(false);
  const [pincodeData, setPincodeData] = useState({ city: "", state: "" });
  const [isPincodeValid, setIsPincodeValid] = useState(false);
  const [singleProductData, setSingleProductData] = useState(null); // Data for single product checkout
  const navigate = useNavigate();

  // Check if user is authenticated by verifying token exists and is likely valid
  const isAuthenticated = () => {
    if (!getTokenFromLocalStorage || !getTokenFromLocalStorage.token) {
      return false;
    }
    // Additional check could be added here for token expiration
    return true;
  };

  // Get current cart items (either auth cart or guest cart)
  const getCurrentCartItems = () => {
    // If we're doing a single product checkout, return that product as a cart item
    // Always fetch product details from params, never from cart
    if (singleProductData && productId) {
      return [{
        productId: singleProductData._id,
        productDetails: singleProductData,
        quantity: 1,
        price: singleProductData.price,
        isPreorder: singleProductData.isPreorder || false,
        preorderDeposit: singleProductData.preorderDeposit || 0
      }];
    }
    
    if (isAuthenticated()) {
      return authCartState;
    } else {
      return guestCartState;
    }
  };

  // Fetch single product data if we're doing instant checkout
  useEffect(() => {
    if (productId) {
      console.log("Fetching product with ID:", productId);
      dispatch(getAProduct(productId))
        .catch(error => {
          console.error("Error fetching product:", error);
          // Show an error message to the user
          toast.error("Failed to load product information. Please try again.");
        });
    }
  }, [productId, dispatch]);

  // Set single product data when it's fetched
  useEffect(() => {
    if (productState && productId) {
      console.log("Product state updated:", productState);
      // Create a product structure that matches what we expect in the cart
      // Always use product data from params/state, never from cart
      const productData = {
        _id: productState._id,
        title: productState.title,
        price: productState.price,
        originalPrice: productState.originalPrice,
        discountedPrice: productState.discountedPrice,
        isPreorder: productState.isPreorder || false,
        preorderDeposit: productState.preorderDeposit || 0,
        preorderReleaseDate: productState.preorderReleaseDate || null,
        images: productState.images || []
      };
      
      setSingleProductData(productData);
      setHasPreorderItems(productState.isPreorder || false);
      // Update total amount calculation to use discounted price if available
      const finalPrice = productState.discountedPrice || productState.price;
      setTotalAmount(finalPrice);
      
      // Track InitiateCheckout for instant checkout
      const cartItem = [{
        productId: productState._id,
        productDetails: productData,
        quantity: 1,
        price: finalPrice
      }];
      trackInitiateCheckout(cartItem, finalPrice);
    } else if (productId && !productState) {
      console.log("Product not found for ID:", productId);
      // Show an error message to the user
      toast.error("Product not found. Please try again with a different product.");
    }
  }, [productState, productId]);

  useEffect(() => {
    let sum = 0;
    let preorder = false;
    
    const currentCart = getCurrentCartItems();
    console.log("Calculating total for cart:", currentCart);
    
    for (let index = 0; index < currentCart?.length; index++) {
      const item = currentCart[index];
      // Always use product data from params, never from cart
      // Use discounted price if available, otherwise use regular price
      let itemPrice = 0;
      if (singleProductData) {
        itemPrice = singleProductData.discountedPrice || singleProductData.price;
      } else if (isAuthenticated()) {
        itemPrice = item?.productId?.discountedPrice || item?.productId?.price || item?.price || 0;
      } else {
        itemPrice = item?.productDetails?.discountedPrice || item?.productDetails?.price || item?.price || 0;
      }
      
      const quantity = item.quantity || 1;
      const itemTotal = Number(quantity) * Number(itemPrice);
      
      console.log(`Item ${index}: quantity=${quantity}, price=${itemPrice}, total=${itemTotal}`);
      
      sum = sum + itemTotal;
      
      // Check if any items are preorder items
      // Always check product data from params, never from cart
      if (singleProductData) {
        if (singleProductData.isPreorder) {
          preorder = true;
        }
      } else if (isAuthenticated()) {
        if (item?.productId?.isPreorder) {
          preorder = true;
        }
      } else {
        if (item?.productDetails?.isPreorder) {
          preorder = true;
        }
      }
    }
    
    console.log("Final total amount:", sum, "Preorder items:", preorder);
    
    // For single product checkout, use the product price directly
    if (singleProductData) {
      const finalPrice = singleProductData.discountedPrice || singleProductData.price;
      setTotalAmount(finalPrice);
    } else {
      setTotalAmount(sum);
    }
    setHasPreorderItems(preorder);
  }, [authCartState, guestCartState, singleProductData]);

  const getTokenFromLocalStorage = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  const config2 = {
    headers: {
      Authorization: getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : "",
      Accept: "application/json",
    },
  };

  useEffect(() => {
    // Only fetch cart if we're not doing single product checkout
    if (!productId) {
      console.log("Fetching cart data");
      if (isAuthenticated()) {
        dispatch(getUserCart(config2));
      } else {
        dispatch(getUserCart()); // For guest users
      }
    }
    
    if (getTokenFromLocalStorage) {
      console.log("Fetching user addresses");
      dispatch(getUserAddresses(config2));
    }
  }, [dispatch, productId]);

  useEffect(() => {
    console.log("Auth state updated:", authState);
    
    if (
      authState?.orderedProduct?.order !== null &&
      authState?.orderedProduct?.success === true
    ) {
      console.log("Order created successfully");
      // For guest users, show signup prompt
      const customer = localStorage.getItem("customer");
      if (!customer) {
        setOrderId(authState?.orderedProduct?.order?._id);
        setShowSignupPrompt(true);
      } else {
        navigate("/my-orders");
      }
    }
  }, [authState]);

  const [cartProductState, setCartProductState] = useState([]);

  useEffect(() => {
    let items = [];
    const currentCart = getCurrentCartItems();
    
    console.log("Current cart items:", currentCart);
    
    for (let index = 0; index < currentCart?.length; index++) {
      const item = currentCart[index];
      
      if (productId) {
        // For single product checkout, item structure matches guest cart item
        console.log("Processing single product item:", item);
        items.push({
          product: item.productId,
          quantity: item.quantity || 1, // Ensure quantity is at least 1
          price: item.price,
          originalPrice: item.productDetails?.originalPrice,
          discountedPrice: item.productDetails?.discountedPrice,
          isPreorder: item.isPreorder || false,
          preorderDeposit: item.preorderDeposit || 0,
        });
      } else if (isAuthenticated()) {
        // For authenticated users with regular cart
        console.log("Processing authenticated user cart item:", item);
        items.push({
          product: item.productId._id,
          quantity: item.quantity,
          price: item.price,
          originalPrice: item.productId.originalPrice,
          discountedPrice: item.productId.discountedPrice,
          isPreorder: item.productId.isPreorder || false,
          preorderDeposit: item.productId.preorderDeposit || 0,
        });
      } else {
        // For guest users with regular cart
        console.log("Processing guest user cart item:", item);
        items.push({
          product: item.productId,
          quantity: item.quantity,
          price: item.productDetails?.price || item.price || 0,
          originalPrice: item.productDetails?.originalPrice,
          discountedPrice: item.productDetails?.discountedPrice,
          isPreorder: item.productDetails?.isPreorder || false,
          preorderDeposit: item.productDetails?.preorderDeposit || 0,
        });
      }
    }
    console.log("Setting cart product state to:", items);
    setCartProductState(items);
  }, [authCartState, guestCartState, singleProductData, productId]);

  // Formik for normal user checkout
  const normalFormik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      address: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
      other: "",
    },
    validationSchema: shippingSchema,
    onSubmit: (values) => {
      // Add city and state from pincode data
      const submissionValues = {
        ...values,
        city: pincodeData.city,
        state: pincodeData.state,
        country: "India"
      };
      setShippingInfo(submissionValues);
      localStorage.setItem("address", JSON.stringify(submissionValues));
      
      // Save address only if user is logged in AND (editing an address OR saveAddressForFuture is checked)
      if (getTokenFromLocalStorage && (editingAddressId || saveAddressForFuture)) {
        const addressToSave = {
          firstname: submissionValues.firstname,
          lastname: submissionValues.lastname,
          address: submissionValues.address,
          state: submissionValues.state,
          city: submissionValues.city,
          country: submissionValues.country,
          pincode: submissionValues.pincode,
          other: submissionValues.other,
          // Only set as default if it's the first address and we're adding (not editing)
          isDefault: !editingAddressId && userAddresses?.length === 0
        };
        
        dispatch(saveUserAddress(addressToSave, config2));
      }
      
      // If not editing, proceed with checkout
      if (!editingAddressId) {
        setTimeout(() => {
          checkOutHandler();
        }, 300);
      } else {
        // If editing, just close the form
        setShowNewAddressForm(false);
        setEditingAddressId(null);
      }
    },
    enableReinitialize: true // Important for form reinitialization
  });

  // Formik for guest user checkout
  const guestFormik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      email: "",
      mobile: "",
      address: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
      other: "",
    },
    validationSchema: guestCheckoutSchema,
    onSubmit: (values) => {
      // Add city and state from pincode data
      const submissionValues = {
        ...values,
        city: pincodeData.city,
        state: pincodeData.state,
        country: "India"
      };
      setShippingInfo(submissionValues);
      localStorage.setItem("address", JSON.stringify(submissionValues));
      
      // Proceed with guest checkout
      setTimeout(() => {
        guestCheckOutHandler(submissionValues);
      }, 300);
    },
    enableReinitialize: true
  });

  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Handler for normal user checkout
  const checkOutHandler = async () => {
    console.log("Starting normal checkout");
    console.log("Has preorder items:", hasPreorderItems);
    console.log("Cart product state:", cartProductState);
    console.log("Total amount:", totalAmount);
    console.log("Shipping info:", shippingInfo);
    
    // For preorder items, we don't process payment immediately
    if (hasPreorderItems) {
      console.log("Processing preorder items");
      // Create order directly without payment for preorder items
      const fakePaymentInfo = {
        razorpayPaymentId: "preorder_payment_pending",
        razorpayOrderId: "preorder_order_pending",
      };

      const orderData = {
        totalPrice: totalAmount,
        totalPriceAfterDiscount: totalAmount,
        orderItems: cartProductState,
        paymentInfo: fakePaymentInfo,
        shippingInfo: shippingInfo || JSON.parse(localStorage.getItem("address")),
      };
      
      console.log("Creating preorder with data:", orderData);

      dispatch(
        createAnOrder(orderData)
      );
      
      // Track Purchase event for instant checkout preorder
      trackPurchase(orderData);
      
      // For guest users, don't clear guest user data immediately
      // It will be cleared after successful registration/login
      const customer = localStorage.getItem("customer");
      if (!customer) {
        // Set order ID for later use in registration/login
        setOrderId(authState?.orderedProduct?.order?._id);
        setShowSignupPrompt(true);
      }
      
      dispatch(deleteUserCart(config2));
      localStorage.removeItem("address");
      dispatch(resetState());
      return;
    }

    // For regular items, proceed with normal payment flow
    console.log("Processing regular items");
    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      toast.error("Razorpay SDK failed to load");
      return;
    }
    
    // Calculate total amount (excluding shipping as it's free)
    const amountToCharge = totalAmount;
    
    // Use appropriate config based on user authentication status
    const paymentConfig = getTokenFromLocalStorage ? config : {};
    
    console.log("Checking out with amount:", amountToCharge);
 const result = await axios.post(
      `${base_url}user/order/checkout`,
      { 
        amount: amountToCharge,
        orderItems: cartProductState,
        shippingInfo: shippingInfo || JSON.parse(localStorage.getItem("address")),
        user: userState?._id || JSON.parse(localStorage.getItem("guestUser"))?._id
      },
      paymentConfig
    );

    if (!result) {
      toast.error("Something went wrong");
      return;
    }

    const { amount, id: order_id, currency } = result.data.order;

    const options = {
      key: result.data.key_id, // Use key_id from backend response
      amount: amount,
      currency: currency,
      name: "VEDA",
      description: "Test Transaction",
      order_id: order_id,
      handler: async function (response) {
        const data = {
          orderCreationId: order_id,
          razorpayPaymentId: response.razorpay_payment_id,
          razorpayOrderId: response.razorpay_order_id,
          razorpaySignature: response.razorpay_signature,
        };

        const result = await axios.post(
          `${base_url}user/order/paymentVerification`,
          data,
          paymentConfig
        );
        
        // Track AddPaymentInfo event for instant checkout
        trackAddPaymentInfo(totalAmount);

        const orderData = {
          totalPrice: totalAmount,
          totalPriceAfterDiscount: totalAmount,
          orderItems: cartProductState,
          paymentInfo: result.data,
          shippingInfo: shippingInfo || JSON.parse(localStorage.getItem("address")),
        };
        
        console.log("Creating order with data:", orderData);

        dispatch(
          createAnOrder(orderData)
        );
        
        // Track Purchase event for instant checkout
        trackPurchase(orderData);
        
        // For guest users, don't clear guest user data immediately
        // It will be cleared after successful registration/login
        const customer = localStorage.getItem("customer");
        if (!customer) {
          // Set order ID for later use in registration/login
          setOrderId(authState?.orderedProduct?.order?._id);
          setShowSignupPrompt(true);
        }
        
        dispatch(deleteUserCart(config2));
        localStorage.removeItem("address");
        dispatch(resetState());
      },
      prefill: {
        name: "VEDA Corner",
        email: userState?.email || "guest@example.com",
        contact: userState?.mobile || "9999999999",
      },
      notes: {
        address: "VEDA Merchandise office",
      },
      theme: {
        color: "#dc3545",
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };


  // Function to check if guest user already exists
  const checkGuestUserExists = async (email, mobile) => {
    try {
      // Make a request to check if a user with this email and mobile already exists
      const response = await axios.post(`${base_url}user/check-guest-exists`, {
        email: email,
        mobile: mobile
      });
      
      // Return the full response data instead of just exists flag
      return response.data;
    } catch (error) {
      // If there's an error (like invalid email format), return false
      // The main validation will catch these issues separately
      return { exists: false, emailExists: false, mobileExists: false };
    }
  };

  // Handler for guest user checkout
  const guestCheckOutHandler = async (guestData) => {
    console.log("Starting guest checkout with data:", guestData);
    console.log("Cart product state:", cartProductState);
    console.log("Total amount:", totalAmount);
    
    // First, validate guest data before proceeding to payment
    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(guestData.email)) {
        toast.error("Invalid email format. Please enter a valid email address.");
        return;
      }
      
      // Validate mobile format (basic validation for Indian numbers)
      const mobileRegex = /^\+?[1-9][\d]{9,14}$/;
      const cleanMobile = guestData.mobile.replace(/[-\s]/g, '').replace(/^\+91/, '');
      if (!mobileRegex.test('+91' + cleanMobile) || cleanMobile.length !== 10) {
        toast.error("Invalid mobile number format. Please enter a valid 10-digit Indian mobile number.");
        return;
      }
      
      // Check if user already exists with this email and mobile
      const userExistsResult = await checkGuestUserExists(guestData.email, guestData.mobile);
      if (userExistsResult.exists) {
        if (userExistsResult.emailExists && userExistsResult.mobileExists) {
          toast.warn("A user with this email and mobile number already exists. Please login or use different email or mobile number.");
        } else if (userExistsResult.emailExists) {
          toast.warn("A user with this email already exists. Please login or use a different email.");
        } else if (userExistsResult.mobileExists) {
          toast.warn("A user with this mobile number already exists. Please login or use a different mobile number.");
        }
        return;
      }
    } catch (validationError) {
      console.error("Validation error during guest checkout:", validationError);
      toast.error("Validation failed: " + (validationError.message || "Please check your information."));
      return;
    }
    
    // For preorder items, we don't process payment immediately
    if (hasPreorderItems) {
      console.log("Processing preorder item");
      // Create order directly without payment for preorder items
      const fakePaymentInfo = {
        razorpayPaymentId: "preorder_payment_pending",
        razorpayOrderId: "preorder_order_pending",
      };

      console.log("Creating preorder with data:", {
        totalPrice: totalAmount,
        totalPriceAfterDiscount: totalAmount,
        orderItems: cartProductState,
        paymentInfo: fakePaymentInfo,
        shippingInfo: guestData
      });

      // Create guest user and place order
      try {
        const guestUserResponse = await authService.createGuestUser({
          firstname: guestData.firstname,
          lastname: guestData.lastname,
          email: guestData.email,
          mobile: guestData.mobile
        });

        console.log("Guest user response:", guestUserResponse);

        if (guestUserResponse && guestUserResponse._id) {
          // Store guest user info in localStorage
          localStorage.setItem("guestUser", JSON.stringify(guestUserResponse));

          const orderData = {
            totalPrice: totalAmount,
            totalPriceAfterDiscount: totalAmount,
            orderItems: cartProductState,
            paymentInfo: fakePaymentInfo,
            shippingInfo: guestData,
            guestUserId: guestUserResponse._id // Pass guest user ID
          };

          dispatch(
            createAnOrder(orderData)
          );
          
          // Track Purchase event for guest instant checkout preorder
          trackPurchase(orderData);
      
          // Set order ID for later use in registration/login
          setOrderId(authState?.orderedProduct?.order?._id);
          // Show login popup instead of signup prompt
          setShowLoginPrompt(true); // Changed from setShowSignupPrompt to setShowLoginPrompt
          setGuestEmail(guestData.email); // Store guest email for login popup
      
          // Clear guest cart
          localStorage.removeItem("guestCart");
          dispatch(resetState());
          return;
        } else {
          throw new Error("Failed to create guest user");
        }
      } catch (error) {
        console.error("Error creating guest user:", error);
        console.error("Error response:", error.response);
        toast.error("Failed to create guest user or place order. Please try again. Error: " + (error.response?.data?.message || error.message || error.toString()));
      }
      return;
    }

    // For regular items, proceed with normal payment flow
    console.log("Processing regular item");
    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      toast.error("Razorpay SDK failed to load");
      return;
    }
    
    // Calculate total amount (excluding shipping as it's free)
    const amountToCharge = totalAmount;
    
    const result = await axios.post(
      `${base_url}user/order/checkout`,
      { 
        amount: amountToCharge,
        orderItems: cartProductState,
        shippingInfo: guestData,
        user: JSON.parse(localStorage.getItem("guestUser"))?._id
      }
    );    if (!result) {
      toast.error("Something went wrong");
      return;
    }

    const { amount, id: order_id, currency } = result.data.order;

    const options = {
      key: result.data.key_id, // Use key_id from backend response
      amount: amount,
      currency: currency,
      name: "VEDA",
      description: "Test Transaction",
      order_id: order_id,
      handler: async function (response) {
        const data = {
          orderCreationId: order_id,
          razorpayPaymentId: response.razorpay_payment_id,
          razorpayOrderId: response.razorpay_order_id,
          razorpaySignature: response.razorpay_signature,
        };

        const paymentResult = await axios.post(
          `${base_url}user/order/paymentVerification`,
          data
        );
        
        // Track AddPaymentInfo event for guest instant checkout
        trackAddPaymentInfo(totalAmount);

        console.log("Creating order with data:", {
          totalPrice: totalAmount,
          totalPriceAfterDiscount: totalAmount,
          orderItems: cartProductState,
          paymentInfo: paymentResult.data,
          shippingInfo: guestData
        });

        // Create guest user AFTER payment success and BEFORE order creation
        try {
          const guestUserResponse = await authService.createGuestUser({
            firstname: guestData.firstname,
            lastname: guestData.lastname,
            email: guestData.email,
            mobile: guestData.mobile
          });

          console.log("Guest user response:", guestUserResponse);

          if (guestUserResponse && guestUserResponse._id) {
            // Store guest user info in localStorage
            localStorage.setItem("guestUser", JSON.stringify(guestUserResponse));

            const orderData = {
              totalPrice: totalAmount,
              totalPriceAfterDiscount: totalAmount,
              orderItems: cartProductState,
              paymentInfo: paymentResult.data,
              shippingInfo: guestData,
              guestUserId: guestUserResponse._id // Pass guest user ID
            };

            dispatch(
              createAnOrder(orderData)
            );
            
            // Track Purchase event for guest instant checkout
            trackPurchase(orderData);
            
            // Set order ID for later use in registration/login
            setOrderId(authState?.orderedProduct?.order?._id);
            // Show login popup instead of signup prompt
            setShowLoginPrompt(true); // Changed from setShowSignupPrompt to setShowLoginPrompt
            setGuestEmail(guestData.email); // Store guest email for login popup
            
            // Clear guest cart
            localStorage.removeItem("guestCart");
            dispatch(resetState());
          } else {
            throw new Error("Failed to create guest user");
          }
        } catch (error) {
          console.error("Error creating guest user after payment:", error);
          console.error("Error response:", error.response);
          toast.error("Your payment was successful, but we encountered an issue creating your account. Please contact support and provide your email address to track your order.");
        }
      },
      prefill: {
        name: guestData.firstname + " " + guestData.lastname,
        email: guestData.email,
        contact: guestData.mobile,
      },
      notes: {
        address: "VEDA Merchandise office",
      },
      theme: {
        color: "#dc3545",
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Handle signup after guest order
  const handleSignup = () => {
    navigate("/signup", { state: { fromGuestOrder: true, orderId } });
  };

  // Handle login after guest order
  const handleLogin = () => {
    navigate("/login", { state: { fromGuestOrder: true, orderId } });
  };

  // Close signup prompt
  const closeSignupPrompt = () => {
    setShowSignupPrompt(false);
    navigate("/my-orders");
  };

  // Close login prompt
  const closeLoginPrompt = () => {
    setShowLoginPrompt(false);
    navigate("/my-orders");
  };

  // Reset form when showing new address form
  useEffect(() => {
    if (showNewAddressForm || editingAddressId) {
      if (editingAddressId && userAddresses) {
        // Populate form with address data when editing
        const addressToEdit = userAddresses.find(addr => addr._id === editingAddressId);
        if (addressToEdit) {
          normalFormik.setValues({
            firstname: addressToEdit.firstname || "",
            lastname: addressToEdit.lastname || "",
            address: addressToEdit.address || "",
            state: addressToEdit.state || "",
            city: addressToEdit.city || "",
            country: addressToEdit.country || "",
            pincode: addressToEdit.pincode || "",
            other: addressToEdit.other || "",
          });
        }
      } else {
        // Clear form when adding new address
        normalFormik.resetForm();
      }
    }
  }, [showNewAddressForm, editingAddressId, userAddresses]);

  // Function to fetch city and state from pincode
  const fetchLocationFromPincode = async (pincode) => {
    if (!pincode || pincode.toString().length !== 6) {
      setPincodeData({ city: "", state: "" });
      setIsPincodeValid(false);
      return;
    }

    try {
      // Using India Post API to get location data from pincode
      const response = await axios.get("https://api.postalpincode.in/pincode/" + pincode);
      if (response.data && response.data[0].Status === "Success") {
        const postOffice = response.data[0].PostOffice[0];
        setPincodeData({
          city: postOffice.District,
          state: postOffice.State
        });
        setIsPincodeValid(true);
        
        // Auto-fill city and state in both forms
        guestFormik.setFieldValue("city", postOffice.District);
        guestFormik.setFieldValue("state", postOffice.State);
        normalFormik.setFieldValue("city", postOffice.District);
        normalFormik.setFieldValue("state", postOffice.State);
      } else {
        setPincodeData({ city: "", state: "" });
        setIsPincodeValid(false);
      }
    } catch (error) {
      console.error("Error fetching pincode data:", error);
      setPincodeData({ city: "", state: "" });
      setIsPincodeValid(false);
    }
  };

  // Add a new function to handle placing order when address is selected
  const handlePlaceOrder = () => {
    // Get the current form values
    const values = normalFormik.values;
    
    // Set shipping info
    setShippingInfo(values);
    localStorage.setItem("address", JSON.stringify(values));
    
    // Save address only if user is logged in AND saveAddressForFuture is checked
    if (getTokenFromLocalStorage && saveAddressForFuture) {
      const addressToSave = {
        firstname: values.firstname,
        lastname: values.lastname,
        address: values.address,
        state: values.state,
        city: values.city,
        country: values.country,
        pincode: values.pincode,
        other: values.other,
        isDefault: userAddresses?.length === 0 // Set as default if it's the first address
      };
      
      dispatch(saveUserAddress(addressToSave, config2));
    }
    
    // Proceed with checkout
    setTimeout(() => {
      checkOutHandler();
    }, 300);
  };

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Checkout - VEDA Merchandise",
    "description": "Complete your purchase at VEDA Merchandise. Secure checkout process for mythological toys, games, and puzzles.",
    "url": "https://vedamerchandise.com/checkout",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="Checkout - VEDA Merchandise"
        description="Complete your purchase at VEDA Merchandise. Secure checkout process for mythological toys, games, and puzzles."
        keywords="checkout, secure checkout, VEDA checkout, mythological toys checkout"
        author="VEDA Merchandise"
        ogTitle="Checkout - VEDA Merchandise"
        ogDescription="Complete your purchase at VEDA Merchandise. Secure checkout process for mythological toys, games, and puzzles."
        ogImage="https://vedamerchandise.com/checkout-preview.jpg"
        ogUrl="https://vedamerchandise.com/checkout"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="Checkout - VEDA Merchandise"
        twitterDescription="Complete your purchase at VEDA Merchandise. Secure checkout process for mythological toys, games, and puzzles."
        twitterImage="https://vedamerchandise.com/checkout-preview.jpg"
        robots="noindex, nofollow"
        canonicalUrl="https://vedamerchandise.com/checkout"
        schemaMarkup={schemaMarkup}
      />
      {/* Signup Prompt Modal */}
      {showSignupPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BsCheckCircle className="text-green-500 text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Placed Successfully!</h2>
              <p className="text-gray-600 mb-6">
                Your order {authState?.orderedProduct?.order?.customOrderId || `#${orderId?.slice(-8).toUpperCase()}`} has been placed successfully.
                Create an account to view your order history and manage your purchases.
              </p>
              <div className="space-y-4">
                <button
                  onClick={handleSignup}
                  className="w-full py-3 bg-[#e91e63] text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all"
                >
                  Create Account
                </button>
                <button
                  onClick={handleLogin}
                  className="w-full py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-full hover:bg-gray-50 transition-all"
                >
                  Login to Existing Account
                </button>
                <button
                  onClick={closeSignupPrompt}
                  className="text-gray-500 hover:text-gray-700 font-semibold"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Prompt Modal for Guest Users */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BsCheckCircle className="text-green-500 text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Placed Successfully!</h2>
              <p className="text-gray-600 mb-4">
                Your order has been placed successfully. Please login with the email you provided to track your order.
              </p>
              <div className="bg-gray-100 rounded-2xl p-4 mb-6">
                <p className="font-semibold text-gray-900">Email:</p>
                <p className="text-lg font-bold text-[#e91e63]">{guestEmail}</p>
                <p className="text-sm text-gray-600 mt-2">Password - You can create your own password by clicking on forget Password link in Login page</p>
              </div>
              <div className="space-y-4">
                <button
                  onClick={handleLogin}
                  className="w-full py-3 bg-[#e91e63] text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all"
                >
                  Login Now
                </button>
                <button
                  onClick={closeLoginPrompt}
                  className="text-gray-500 hover:text-gray-700 font-semibold"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-red-50 via-yellow-50 to-red-50 py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Checkout</h1>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center space-x-2 md:space-x-4 mb-6">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <BsCheckCircle className="text-white" />
              </div>
              <span className="ml-2 text-sm font-semibold text-green-600 hidden md:inline">Cart</span>
            </div>
            <div className="h-0.5 w-12 md:w-20 bg-red-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">2</span>
              </div>
              <span className="ml-2 text-sm font-semibold text-red-600 hidden md:inline">Information</span>
            </div>
            <div className="h-0.5 w-12 md:w-20 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <BsCreditCard className="text-gray-600" />
              </div>
              <span className="ml-2 text-sm font-semibold text-gray-500 hidden md:inline">Payment</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Shipping Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8">
              {/* Contact Information */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>
                {!isAuthenticated() && !isGuestCheckout && (
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setIsGuestCheckout(true);
                        setDisableNormalForm(true);
                      }}
                      className="w-full py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all"
                    >
                      Checkout as Guest
                    </button>
                  </div>
                )}
                {isAuthenticated() && (
                  <div className="bg-gradient-to-r from-red-50 to-yellow-50 rounded-2xl p-4">
                    <p className="text-gray-700 font-medium">
                      {userState?.email || "youremail@gmail.com"}
                    </p>
                  </div>
                )}
              </div>

              {/* Preorder Notice */}
              {hasPreorderItems && (
                <div className="mb-6 p-4 bg-yellow-600 bg-opacity-10 border rounded-3xl">
                  <div className="flex items-start gap-3">
                    <BsCalendarEvent className=" mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h3 className="font-bold mb-1">Preorder Items in Your Cart</h3>
                      <p className="text-sm">
                        Your order contains preorder items. Payment will be processed in full when all items are in stock and ready to ship.
                        You'll receive updates about your order status via email.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Guest Checkout Form */}
              {!isAuthenticated() && isGuestCheckout && (
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-900">Guest Checkout</h2>
                    <button
                      type="button"
                      onClick={() => {
                        setIsGuestCheckout(false);
                        setDisableNormalForm(false);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                  
                  <form onSubmit={guestFormik.handleSubmit} className="space-y-6">
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <BsEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          placeholder="your@email.com"
                          value={guestFormik.values.email}
                          onChange={guestFormik.handleChange}
                          onBlur={guestFormik.handleBlur}
                          disabled={false}
                          className={`w-full pl-10 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                            guestFormik.touched.email && guestFormik.errors.email
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        />
                      </div>
                      {guestFormik.touched.email && guestFormik.errors.email && (
                        <p className="mt-2 text-sm text-red-600">{guestFormik.errors.email}</p>
                      )}
                    </div>

                    {/* Mobile */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Mobile Number *
                      </label>
                      <div className="relative">
                        <BsTelephone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="mobile"
                          placeholder="Mobile Number"
                          value={guestFormik.values.mobile}
                          onChange={guestFormik.handleChange}
                          onBlur={guestFormik.handleBlur}
                          disabled={false}
                          className={`w-full pl-10 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                            guestFormik.touched.mobile && guestFormik.errors.mobile
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        />
                      </div>
                      {guestFormik.touched.mobile && guestFormik.errors.mobile && (
                        <p className="mt-2 text-sm text-red-600">{guestFormik.errors.mobile}</p>
                      )}
                    </div>

                    {/* First Name & Last Name */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          First Name *
                        </label>
                        <div className="relative">
                          <BsPerson className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            name="firstname"
                            placeholder="First Name"
                            value={guestFormik.values.firstname}
                            onChange={guestFormik.handleChange}
                            onBlur={guestFormik.handleBlur}
                            disabled={false}
                            className={`w-full pl-10 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                              guestFormik.touched.firstname && guestFormik.errors.firstname
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          />
                        </div>
                        {guestFormik.touched.firstname && guestFormik.errors.firstname && (
                          <p className="mt-2 text-sm text-red-600">{guestFormik.errors.firstname}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <div className="relative">
                          <BsPerson className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            name="lastname"
                            placeholder="Last Name"
                            value={guestFormik.values.lastname}
                            onChange={guestFormik.handleChange}
                            onBlur={guestFormik.handleBlur}
                            disabled={false}
                            className={"w-full pl-10 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all " + (
                              guestFormik.touched.lastname && guestFormik.errors.lastname
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            )}
                          />
                        </div>
                        {guestFormik.touched.lastname && guestFormik.errors.lastname && (
                          <p className="mt-2 text-sm text-red-600">{guestFormik.errors.lastname}</p>
                        )}
                      </div>
                    </div>

                    {/* Hidden Country Field - Fixed to India */}
                    <input
                      type="hidden"
                      name="country"
                      value="India"
                    />

                    {/* Address */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Address *
                      </label>
                      <input
                        type="text"
                        name="address"
                        placeholder="Street address"
                        value={guestFormik.values.address}
                        onChange={guestFormik.handleChange}
                        onBlur={guestFormik.handleBlur}
                        disabled={false}
                        className={"w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all " + (
                          guestFormik.touched.address && guestFormik.errors.address
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        )}
                      />
                      {guestFormik.touched.address && guestFormik.errors.address && (
                        <p className="mt-2 text-sm text-red-600">{guestFormik.errors.address}</p>
                      )}
                    </div>

                    {/* Apartment/Suite */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Apartment, Suite, etc. (Optional)
                      </label>
                      <input
                        type="text"
                        name="other"
                        placeholder="Apartment, suite, etc."
                        value={guestFormik.values.other}
                        onChange={guestFormik.handleChange}
                        onBlur={guestFormik.handleBlur}
                        disabled={false}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-300"
                      />
                    </div>

                    {/* Pincode with auto-fetch */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        placeholder="Pincode"
                        value={guestFormik.values.pincode}
                        onChange={(e) => {
                          const pincode = e.target.value;
                          guestFormik.handleChange(e);
                          if (pincode.length === 6) {
                            fetchLocationFromPincode(pincode);
                          }
                        }}
                        onBlur={guestFormik.handleBlur}
                        disabled={false}
                        className={"w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all " + (
                          guestFormik.touched.pincode && guestFormik.errors.pincode
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        )}
                      />
                      {!isPincodeValid && guestFormik.values.pincode.length === 6 && (
                        <p className="mt-2 text-sm text-red-600">Invalid pincode</p>
                      )}
                      {isPincodeValid && (
                        <p className="mt-2 text-sm text-green-600">
                          {pincodeData.city}, {pincodeData.state}
                        </p>
                      )}
                      {guestFormik.touched.pincode && guestFormik.errors.pincode && (
                        <p className="mt-2 text-sm text-red-600">{guestFormik.errors.pincode}</p>
                      )}
                    </div>

                    {/* Hidden Country Field - Fixed to India */}
                    <input
                      type="hidden"
                      name="country"
                      value="India"
                    />
                    
                    {/* Hidden fields for city and state */}
                    <input
                      type="hidden"
                      name="city"
                      value={pincodeData.city}
                    />
                    <input
                      type="hidden"
                      name="state"
                      value={pincodeData.state}
                    />
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-6">
                      <button
                        type="button"
                        onClick={() => {
                          setIsGuestCheckout(false);
                          setDisableNormalForm(false);
                        }}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-semibold transition-colors"
                      >
                        <BiArrowBack className="text-xl" />
                        <span>Cancel</span>
                      </button>
                      
                      <button
                        type="submit"
                        disabled={false}
                        className="w-full md:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-[#e91e63] text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105"
                      >
                        <BsLock className="text-xl" />
                        <span>
                          {hasPreorderItems ? "Place Preorder" : "Place Order"}
                        </span>
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Address Selection Section - Only for authenticated users */}
              {isAuthenticated() && userAddresses && userAddresses.length > 0 && !showNewAddressForm && !editingAddressId && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Select a Saved Address</h3>
                  <div className="space-y-3">
                    {userAddresses.map((address) => (
                      <div 
                        key={address._id} 
                        className={`border-2 rounded-2xl p-4 cursor-pointer transition-all ${
                          selectedAddressId === address._id 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200 hover:border-red-300'
                        }`}
                        onClick={() => {
                          setSelectedAddressId(address._id);
                          // Populate the main form with selected address
                          normalFormik.setValues({
                            firstname: address.firstname || "",
                            lastname: address.lastname || "",
                            address: address.address || "",
                            state: address.state || "",
                            city: address.city || "",
                            country: address.country || "",
                            pincode: address.pincode || "",
                            other: address.other || "",
                          });
                        }}
                      >
                        <div className="flex items-start">
                          <div className="mt-1 mr-3">
                            <input
                              type="radio"
                              name="selectedAddress"
                              checked={selectedAddressId === address._id}
                              onChange={() => {
                                setSelectedAddressId(address._id);
                                // Populate the main form with selected address
                                normalFormik.setValues({
                                  firstname: address.firstname || "",
                                  lastname: address.lastname || "",
                                  address: address.address || "",
                                  state: address.state || "",
                                  city: address.city || "",
                                  country: address.country || "",
                                  pincode: address.pincode || "",
                                  other: address.other || "",
                                });
                              }}
                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            />
                          </div>
                          <div className="flex-grow">
                            <div className="flex justify-between">
                              <h4 className="font-bold text-gray-900">
                                {address.firstname} {address.lastname}
                                {address.isDefault && (
                                  <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                                    Default
                                  </span>
                                )}
                              </h4>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingAddressId(address._id);
                                  setShowNewAddressForm(true);
                                }}
                                className="text-gray-500 hover:text-red-500"
                              >
                                <FiEdit size={16} />
                              </button>
                            </div>
                            <p className="text-gray-600 text-sm">
                              {address.address}
                              {address.other && `, ${address.other}`}
                              <br />
                              {address.city}, {address.state} {address.pincode}
                              <br />
                              {address.country}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* OR divider */}
                  <div className="flex items-center my-6">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="flex-shrink mx-4 text-gray-500 font-medium">OR</span>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>
                  
                  {/* Add New Address Button */}
                  <div className="mb-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewAddressForm(true);
                        setEditingAddressId(null);
                        normalFormik.resetForm();
                      }}
                      className="w-full py-3 bg-white border-2 border-red-500 text-red-500 font-bold rounded-full shadow-lg hover:bg-red-50 transition-all"
                    >
                      Add a New Address
                    </button>
                  </div>
                  
                  {/* Place Order Button when address is selected */}
                  {selectedAddressId && (
                    <div className="flex justify-center pt-4">
                      <button
                        type="button"
                        onClick={handlePlaceOrder}
                        className="w-full md:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-[#e91e63] text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105"
                      >
                        <BsLock className="text-xl" />
                        <span>
                          {hasPreorderItems ? "Place Preorder" : "Place Order"}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Shipping Address Form - Only show when adding new or editing or for guest users who are not in guest checkout */}
              {((showNewAddressForm || editingAddressId || !userAddresses || userAddresses.length === 0) && isAuthenticated()) && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-900">
                      {editingAddressId ? "Edit Address" : 
                       userAddresses && userAddresses.length > 0 ? "Add New Address" : "Shipping Address"}
                    </h2>
                    {(showNewAddressForm || editingAddressId) && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewAddressForm(false);
                          setEditingAddressId(null);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  
                  <form onSubmit={normalFormik.handleSubmit} className="space-y-6">
                    {/* Hidden Country Field - Fixed to India */}
                    <input
                      type="hidden"
                      name="country"
                      value="India"
                    />

                    {/* First Name & Last Name */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="firstname"
                          placeholder="First Name"
                          value={normalFormik.values.firstname}
                          onChange={normalFormik.handleChange}
                          onBlur={normalFormik.handleBlur}
                          disabled={disableNormalForm}
                          className={"w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all " + (
                            normalFormik.touched.firstname && normalFormik.errors.firstname
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          )}
                        />
                        {normalFormik.touched.firstname && normalFormik.errors.firstname && (
                          <p className="mt-2 text-sm text-red-600">{normalFormik.errors.firstname}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="lastname"
                          placeholder="Last Name"
                          value={normalFormik.values.lastname}
                          onChange={normalFormik.handleChange}
                          onBlur={normalFormik.handleBlur}
                          disabled={disableNormalForm}
                          className={"w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all " + (
                            normalFormik.touched.lastname && normalFormik.errors.lastname
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          )}
                        />
                        {normalFormik.touched.lastname && normalFormik.errors.lastname && (
                          <p className="mt-2 text-sm text-red-600">{normalFormik.errors.lastname}</p>
                        )}
                      </div>
                    </div>

                    {/* Address */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Address *
                      </label>
                      <input
                        type="text"
                        name="address"
                        placeholder="Street address"
                        value={normalFormik.values.address}
                        onChange={normalFormik.handleChange}
                        onBlur={normalFormik.handleBlur}
                        disabled={disableNormalForm}
                        className={"w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all " + (
                          normalFormik.touched.address && normalFormik.errors.address
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        )}
                      />
                      {normalFormik.touched.address && normalFormik.errors.address && (
                        <p className="mt-2 text-sm text-red-600">{normalFormik.errors.address}</p>
                      )}
                    </div>

                    {/* Apartment/Suite */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Apartment, Suite, etc. (Optional)
                      </label>
                      <input
                        type="text"
                        name="other"
                        placeholder="Apartment, suite, etc."
                        value={normalFormik.values.other}
                        onChange={normalFormik.handleChange}
                        onBlur={normalFormik.handleBlur}
                        disabled={disableNormalForm}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-300"
                      />
                    </div>

                    {/* Pincode with auto-fetch */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        placeholder="Pincode"
                        value={normalFormik.values.pincode}
                        onChange={(e) => {
                          const pincode = e.target.value;
                          normalFormik.handleChange(e);
                          if (pincode.length === 6) {
                            fetchLocationFromPincode(pincode);
                          }
                        }}
                        onBlur={normalFormik.handleBlur}
                        disabled={disableNormalForm}
                        className={"w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all " + (
                          normalFormik.touched.pincode && normalFormik.errors.pincode
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        )}
                      />
                      {!isPincodeValid && normalFormik.values.pincode.length === 6 && (
                        <p className="mt-2 text-sm text-red-600">Invalid pincode</p>
                      )}
                      {isPincodeValid && (
                        <p className="mt-2 text-sm text-green-600">
                          {pincodeData.city}, {pincodeData.state}
                        </p>
                      )}
                      {normalFormik.touched.pincode && normalFormik.errors.pincode && (
                        <p className="mt-2 text-sm text-red-600">{normalFormik.errors.pincode}</p>
                      )}
                    </div>

                    {/* Hidden fields for city and state */}
                    <input
                      type="hidden"
                      name="city"
                      value={pincodeData.city}
                    />
                    <input
                      type="hidden"
                      name="state"
                      value={pincodeData.state}
                    />

                    {/* Save Address Checkbox - Only show for logged in users when adding new address */}
                    {getTokenFromLocalStorage && !editingAddressId && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="saveAddress"
                          checked={saveAddressForFuture}
                          onChange={(e) => setSaveAddressForFuture(e.target.checked)}
                          disabled={disableNormalForm}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <label htmlFor="saveAddress" className="ml-2 block text-sm text-gray-700">
                          Save this address for future orders
                        </label>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-6">
                      {/* Cancel button when editing or adding new address */}
                      {(showNewAddressForm || editingAddressId) && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewAddressForm(false);
                            setEditingAddressId(null);
                          }}
                          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-semibold transition-colors"
                        >
                          <BiArrowBack className="text-xl" />
                          <span>Cancel</span>
                        </button>
                      )}
                      
                      {/* Return to cart button when no addresses exist */}
                      {(!showNewAddressForm && !editingAddressId && (!userAddresses || userAddresses.length === 0)) && (
                        <Link
                          to="/cart"
                          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-semibold transition-colors"
                        >
                          <BiArrowBack className="text-xl" />
                          <span>Return to Cart</span>
                        </Link>
                      )}

                      <button
                        type="submit"
                        disabled={disableNormalForm}
                        className="w-full md:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-[#e91e63] text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105"
                      >
                        <BsLock className="text-xl" />
                        <span>
                          {editingAddressId ? "Update Address" : 
                           hasPreorderItems ? "Place Preorder" : "Place Order"}
                        </span>
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl shadow-xl p-6 sticky top-24">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h3>

              {/* Cart Items */}
              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {getCurrentCartItems() &&
                  getCurrentCartItems()?.map((item, index) => {
                    // For single product checkout, we need to handle the item structure differently
                    const isSingleProduct = !!productId;
                    const productTitle = isSingleProduct 
                      ? (item?.productDetails?.title || "Product") 
                      : (isAuthenticated() ? item?.productId?.title : item?.productDetails?.title);
                    const productImage = isSingleProduct
                      ? (item?.productDetails?.images?.[0]?.url || "")
                      : (isAuthenticated() ? item?.productId?.images?.[0]?.url : item?.productDetails?.images?.[0]?.url);
                    const productPrice = isSingleProduct
                      ? (item?.price || 0)
                      : (isAuthenticated() ? item?.price : (item?.productDetails?.price || item?.price || 0));
                    
                    // Get discounted and original prices for display
                    const originalPrice = isSingleProduct
                      ? (item?.productDetails?.originalPrice)
                      : (isAuthenticated() ? item?.productId?.originalPrice : item?.productDetails?.originalPrice);
                    const discountedPrice = isSingleProduct
                      ? (item?.productDetails?.discountedPrice)
                      : (isAuthenticated() ? item?.productId?.discountedPrice : item?.productDetails?.discountedPrice);
                    const isPreorder = isSingleProduct
                      ? (item?.productDetails?.isPreorder || item?.isPreorder || false)
                      : (isAuthenticated() ? item?.productId?.isPreorder : item?.productDetails?.isPreorder);
                    const preorderReleaseDate = isSingleProduct
                      ? (item?.productDetails?.preorderReleaseDate)
                      : (isAuthenticated() ? item?.productId?.preorderReleaseDate : item?.productDetails?.preorderReleaseDate);
                    
                    return (
                      <div key={index} className="flex gap-4 items-start pb-4 border-b border-gray-100">
                        <div className="relative flex-shrink-0">
                          <img
                            src={productImage}
                            className="w-20 h-20 object-cover rounded-2xl"
                            alt={productTitle}
                          />
                          <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                            {item?.quantity || 1}
                          </span>
                          {/* Preorder Badge */}
                          {isPreorder && (
                            <span className="absolute -bottom-2 -right-2 w-6 h-6 bg-[#e91e63] text-white text-xs font-bold rounded-full flex items-center justify-center">
                              P
                            </span>
                          )}
                        </div>
                        <div className="flex-grow">
                          <h4 className="font-semibold text-gray-900 text-sm mb-1">
                            {productTitle}
                          </h4>
                          <p className="text-gray-600 text-xs mb-2">
                            {item?.color?.title}
                          </p>
                          {/* Preorder Info */}
                          {isPreorder && (
                            <div className="mb-2">
                              <span className="inline-block bg-[#e91e63] bg-opacity-10 text-primary text-xs px-2 py-1 rounded-full">
                                Preorder
                              </span>
                              {preorderReleaseDate && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Est. Release: {formatDate(preorderReleaseDate)}
                                </p>
                              )}
                            </div>
                          )}
                          {/* Display discounted price and original price with discount percentage */}
                          {(
                            discountedPrice && 
                            originalPrice && 
                            discountedPrice < originalPrice
                          ) ? (
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-red-500 mb-0">
                                ₹{((item?.quantity || 1) * discountedPrice).toLocaleString()}
                              </p>
                              <p className="text-sm text-gray-500 font-normal mb-0 line-through">
                                ₹{((item?.quantity || 1) * originalPrice).toLocaleString()}
                              </p>
                              <span className="badge bg-danger fs-6 px-2 py-1 rounded-pill">
                                {Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)}% OFF
                              </span>
                            </div>
                          ) : (
                            <p className="font-bold text-red-500">
                              ₹{((item?.quantity || 1) * productPrice).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center pb-3 border-b-2 border-gray-100">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-bold text-gray-900">
                    ₹{totalAmount ? totalAmount.toLocaleString() : "0"}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b-2 border-gray-100">
                  <span className="text-gray-600">Shipping</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900 line-through">₹150</span>
                    <span className="font-bold text-green-600">₹0</span>
                  </div>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-xl font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-red-500">
                    ₹{totalAmount ? totalAmount.toLocaleString() : "0"}
                  </span>
                </div>
              </div>

              {/* Security Info */}
              <div className="space-y-3 p-4 bg-gradient-to-br from-yellow-50 to-red-50 rounded-2xl">
                <div className="flex items-center gap-3">
                  <BsLock className="text-red-500 text-xl" />
                  <p className="text-sm font-semibold text-gray-900">Secure Payment</p>
                </div>
                <div className="flex items-center gap-3">
                  <BsTruck className="text-red-500 text-xl" />
                  <p className="text-sm font-semibold text-gray-900">Fast Delivery</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutInstant;